// Renderer, camera, lighting, sky/fog and time-of-day. Camera constants from
// js/scene-manager.js:370-372; sky palette from v1 COLORS.SKY. v1 tied the
// cycle to the real clock with a KeyT debug override — v2 keeps the KeyT
// cycle and defaults to day.

import * as THREE from 'three';
import {
  CAMERA_FOV,
  CAMERA_NEAR,
  CAMERA_FAR,
  CAMERA_POSITION,
  CAMERA_LOOK_AT,
} from '@/config/tuning';
import { SKY_COLORS } from '@/content/districts';
import { setLampsLit } from '@/world/props';
import { setWindowsNight } from '@/world/buildings';

export type TimeOfDay = 'day' | 'dusk' | 'night' | 'dawn';
const TIME_ORDER: readonly TimeOfDay[] = ['day', 'dusk', 'night', 'dawn'];

interface TimePreset {
  sky: number;
  fog: number;
  sunIntensity: number;
  hemiIntensity: number;
  sunColor: number;
}

const TIME_PRESETS: Record<TimeOfDay, TimePreset> = {
  day: {
    sky: SKY_COLORS.day,
    fog: SKY_COLORS.day,
    sunIntensity: 1.4,
    hemiIntensity: 0.9,
    sunColor: 0xfff4e0,
  },
  dusk: {
    sky: SKY_COLORS.dusk,
    fog: SKY_COLORS.dusk,
    sunIntensity: 0.8,
    hemiIntensity: 0.55,
    sunColor: 0xffb070,
  },
  night: {
    sky: SKY_COLORS.night,
    fog: SKY_COLORS.night,
    sunIntensity: 0.25,
    hemiIntensity: 0.3,
    sunColor: 0x8899ff,
  },
  dawn: {
    sky: SKY_COLORS.dawn,
    fog: SKY_COLORS.dawn,
    sunIntensity: 0.7,
    hemiIntensity: 0.5,
    sunColor: 0xffc0a0,
  },
};

export class Stage {
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  readonly renderer: THREE.WebGLRenderer;
  timeOfDay: TimeOfDay = 'day';

  private readonly baseFOV = CAMERA_FOV;
  private readonly baseCameraY = CAMERA_POSITION.y;
  private readonly baseCameraZ = CAMERA_POSITION.z;
  private targetFOV = CAMERA_FOV;

  private readonly sun: THREE.DirectionalLight;
  private readonly hemi: THREE.HemisphereLight;
  private readonly skyColor = new THREE.Color(SKY_COLORS.day);
  private readonly targetSky = new THREE.Color(SKY_COLORS.day);
  private targetSunIntensity = 1.4;
  private targetHemiIntensity = 0.9;
  private readonly targetSunColor = new THREE.Color(0xfff4e0);

  constructor(container: HTMLElement) {
    this.camera = new THREE.PerspectiveCamera(
      CAMERA_FOV,
      window.innerWidth / window.innerHeight,
      CAMERA_NEAR,
      CAMERA_FAR,
    );
    this.camera.position.set(CAMERA_POSITION.x, CAMERA_POSITION.y, CAMERA_POSITION.z);
    this.camera.lookAt(CAMERA_LOOK_AT.x, CAMERA_LOOK_AT.y, CAMERA_LOOK_AT.z);

    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    container.appendChild(this.renderer.domElement);

    this.scene.background = this.skyColor;
    this.scene.fog = new THREE.Fog(this.skyColor.getHex(), 40, 120);

    // Gradient sky dome (Phase 7): grayscale vertical gradient tinted by the
    // time-of-day color — horizon bright, zenith deep; fog-free, behind all
    this.skyDome = this.buildSkyDome();
    this.scene.add(this.skyDome);

    // Speed lines (v1 scene-manager speedLines): thin streaks that fade in
    // at high speed near the screen edges
    this.speedLines = this.buildSpeedLines();
    this.scene.add(this.speedLines);

    this.hemi = new THREE.HemisphereLight(0xffffff, 0x666666, 0.9);
    this.scene.add(this.hemi);

    // Warm fill light that follows the player (v1 playerLight,
    // scene-manager.js:635-639) — lifts the character off the scene
    this.playerLight = new THREE.PointLight(0xffe8c0, 0.6, 7);
    this.playerLight.position.set(0, 1.4, 1.5);
    this.scene.add(this.playerLight);
    this.sun = new THREE.DirectionalLight(0xfff4e0, 1.4);
    this.sun.position.set(10, 20, 10);
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(1024, 1024);
    this.sun.shadow.camera.left = -15;
    this.sun.shadow.camera.right = 15;
    this.sun.shadow.camera.top = 20;
    this.sun.shadow.camera.bottom = -20;
    this.scene.add(this.sun);

    window.addEventListener('resize', () => this.onResize());
  }

  setTimeOfDay(time: TimeOfDay): void {
    this.timeOfDay = time;
    const preset = TIME_PRESETS[time];
    this.targetSky.setHex(preset.sky);
    this.targetSunIntensity = preset.sunIntensity;
    this.targetHemiIntensity = preset.hemiIntensity;
    this.targetSunColor.setHex(preset.sunColor);
    const dark = time === 'night' || time === 'dusk';
    setLampsLit(dark);
    setWindowsNight(dark);
  }

  /** Cycle day → dusk → night → dawn (v1 KeyT debug binding). */
  cycleTimeOfDay(): void {
    const index = TIME_ORDER.indexOf(this.timeOfDay);
    this.setTimeOfDay(TIME_ORDER[(index + 1) % TIME_ORDER.length] ?? 'day');
  }

  /** Speed-reactive FOV/height/distance — verbatim v1
   *  js/scene-manager.js:741-758 (updateDynamicCamera). */
  updateDynamicCamera(dt: number, gameSpeed: number, playerY: number): void {
    // FOV: 75 base -> up to 85 at high speed
    const speedFactor = Math.min((gameSpeed - 0.1) / 0.15, 1.0);
    this.targetFOV = this.baseFOV + Math.max(0, speedFactor) * 10;
    this.camera.fov += (this.targetFOV - this.camera.fov) * dt * 2;

    // Height: follow player Y by 30% during jumps (0.27 ≈ groundY)
    const targetY = this.baseCameraY + (playerY - 0.27) * 0.3;
    this.camera.position.y += (targetY - this.camera.position.y) * dt * 4;

    // Distance: pull back slightly at high speed (+1.0 max)
    const targetZ = this.baseCameraZ + Math.max(0, speedFactor) * 1.0;
    this.camera.position.z += (targetZ - this.camera.position.z) * dt * 2;

    this.camera.updateProjectionMatrix();
  }

  private combatFocus: THREE.Vector3 | null = null;
  private shakeIntensity = 0;
  private shakeTimer = 0;
  private skyDome!: THREE.Mesh;
  private speedLines!: THREE.Group;
  private playerLight!: THREE.PointLight;

  /** v1 playerLight follow: head height, slightly behind the player. */
  updatePlayerLight(playerPos: THREE.Vector3): void {
    this.playerLight.position.set(playerPos.x, playerPos.y + 1.1, playerPos.z - 0.5);
  }

  private buildSkyDome(): THREE.Mesh {
    // Vertical grayscale gradient; material.color (lerped with the sky)
    // tints it so day/dusk/night all get horizon→zenith depth
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 128;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      const gradient = ctx.createLinearGradient(0, 128, 0, 0);
      gradient.addColorStop(0, '#ffffff'); // Horizon = full sky color
      gradient.addColorStop(1, '#8090a8'); // Zenith = deeper, cooler
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, 1, 128);
    }
    const texture = new THREE.CanvasTexture(canvas);
    const material = new THREE.MeshBasicMaterial({
      map: texture,
      color: this.skyColor,
      side: THREE.BackSide,
      fog: false,
      depthWrite: false,
    });
    material.userData.noToon = true;
    const dome = new THREE.Mesh(new THREE.SphereGeometry(160, 24, 12), material);
    dome.userData.noToon = true;
    dome.renderOrder = -1;
    return dome;
  }

  private buildSpeedLines(): THREE.Group {
    const group = new THREE.Group();
    const geo = new THREE.BoxGeometry(0.03, 0.03, 3.2);
    for (let i = 0; i < 10; i++) {
      const material = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0,
      });
      material.userData.noToon = true;
      const line = new THREE.Mesh(geo, material);
      const side = i % 2 === 0 ? -1 : 1;
      line.position.set(
        side * (2.5 + Math.random() * 3.5),
        1 + Math.random() * 3,
        -20 - Math.random() * 25,
      );
      line.userData.noToon = true;
      group.add(line);
    }
    group.userData.noToon = true;
    return group;
  }

  /** Streaks fade in above ~60% max speed and rush past (v1 speed lines). */
  updateSpeedLines(dt: number, gameSpeed: number): void {
    const intensity = Math.max(0, (gameSpeed - 0.16) / 0.09); // 0 at 0.16, 1 at 0.25
    for (const line of this.speedLines.children) {
      const mesh = line as THREE.Mesh;
      (mesh.material as THREE.MeshBasicMaterial).opacity = intensity * 0.35;
      if (intensity <= 0) continue;
      mesh.position.z += (1.2 + gameSpeed * 4) * dt * 60;
      if (mesh.position.z > 8) {
        mesh.position.z = -35 - Math.random() * 15;
        mesh.position.y = 1 + Math.random() * 3;
      }
    }
  }

  /** Screen shake (v1 sceneManager.shakeCamera; shield hit uses 0.15/0.2). */
  shakeCamera(intensity: number, duration: number): void {
    this.shakeIntensity = intensity;
    this.shakeTimer = duration;
  }

  private applyShake(): void {
    if (this.shakeTimer <= 0) return;
    this.shakeTimer -= 1 / 60;
    const falloff = Math.max(this.shakeTimer, 0) * this.shakeIntensity * 10;
    this.camera.position.x += (Math.random() - 0.5) * falloff;
    this.camera.position.y += (Math.random() - 0.5) * falloff;
  }

  /** Combat camera: zoom toward the encounter (v1 COMBAT_CONFIG.CAMERA:
   *  distance 2.5, focus height 1.2); null returns to the run camera. */
  setCombatFocus(target: THREE.Vector3 | null): void {
    this.combatFocus = target ? target.clone() : null;
  }

  /** Smooth horizontal follow of the player + atmosphere transitions. */
  followPlayer(playerX: number): void {
    if (this.combatFocus) {
      const focus = this.combatFocus;
      this.camera.position.x += (focus.x - this.camera.position.x) * 0.06;
      this.camera.position.y += (1.2 + 0.6 - this.camera.position.y) * 0.06;
      this.camera.position.z += (focus.z + 2.5 - this.camera.position.z) * 0.06;
      this.camera.lookAt(focus.x, 1.2, focus.z);
      this.easeAtmosphere();
      return;
    }
    // Verbatim v1 js/scene-manager.js:616-626: the camera tracks the player's
    // lane fully, faster while switching (0.15) than when settled (0.05)
    const distance = Math.abs(playerX - this.camera.position.x);
    const smoothingFactor = distance > 0.3 ? 0.15 : 0.05;
    this.camera.position.x += (playerX - this.camera.position.x) * smoothingFactor;
    this.camera.lookAt(this.camera.position.x, CAMERA_LOOK_AT.y, CAMERA_LOOK_AT.z);
    this.easeAtmosphere();
  }

  /** Ease sky/fog/lights toward the active time-of-day preset. */
  private easeAtmosphere(): void {
    this.skyColor.lerp(this.targetSky, 0.02);
    if (this.scene.fog instanceof THREE.Fog) this.scene.fog.color.copy(this.skyColor);
    (this.skyDome.material as THREE.MeshBasicMaterial).color.copy(this.skyColor);
    this.sun.intensity += (this.targetSunIntensity - this.sun.intensity) * 0.02;
    this.hemi.intensity += (this.targetHemiIntensity - this.hemi.intensity) * 0.02;
    this.sun.color.lerp(this.targetSunColor, 0.02);
  }

  render(): void {
    this.applyShake();
    this.renderer.render(this.scene, this.camera);
  }

  private onResize(): void {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
  }
}
